/**
 * P4 监管中心 — admin / director / manager
 * 3 tab：L0 / L1 / L2 监管视图 + 大盘 + 通过率排行
 */
import { Tabs } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Pill, StatCard, TableWrap, Avatar } from '../../components/ui';

export function LeadMonitorCenterPage() {
  const { data } = useQuery<any>({
    queryKey: ['lead-monitor-dashboard'],
    queryFn: () => $.get('/lead-monitor-center/dashboard'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        breadcrumb={['管理', '监管中心']}
        title="👁 客户阶段监管中心"
        subtitle="L0 加微信 / L1 触达 / L2 破冰 阶段卡点诊断 · 销售员通过率排行"
      />
      <div className="work-content">
        <Tabs
          items={[
            { key: 'overview', label: '大盘', children: <Overview data={data} /> },
            { key: 'l0', label: 'L0 加微信', children: <StageTab title="L0" stats={data?.l0} /> },
            { key: 'l1', label: 'L1 监管', children: <StageTab title="L1" stats={data?.l1} /> },
            { key: 'l2', label: 'L2 监管', children: <StageTab title="L2" stats={data?.l2} /> },
            { key: 'ranking', label: '通过率排行', children: <Ranking rows={data?.conversionRanking ?? []} /> },
          ]}
        />
      </div>
    </>
  );
}

function Overview({ data }: { data: any }) {
  if (!data) return <div style={{ padding: 24, color: 'var(--text-3)' }}>加载中…</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      <BigCard
        title="L0 加微信"
        total={data.l0.total} overdue={data.l0.overdue}
        threshold={`${data.l0.threshold_hours}h`}
        desc="销售员分配客户后超过阈值仍未加上微信"
      />
      <BigCard
        title="L1 触达监管"
        total={data.l1.total} overdue={data.l1.overdue}
        threshold={`${data.l1.threshold_hours}h`}
        desc="客户进入 L1 后销售员超过阈值未跟进"
      />
      <BigCard
        title="L2 破冰监管"
        total={data.l2.total} overdue={data.l2.overdue}
        threshold={`${data.l2.threshold_days}d`}
        desc="客户进入 L2 后销售员超过阈值未跟进"
      />
    </div>
  );
}

function BigCard({ title, total, overdue, threshold, desc }: any) {
  const pct = total > 0 ? Math.round((overdue / total) * 100) : 0;
  const tone = pct >= 30 ? 'danger' : pct >= 10 ? 'warning' : 'success';
  return (
    <HomeCard title={title}>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 600 }}>{overdue}</span>
          <span style={{ color: 'var(--text-3)' }}>/ {total}</span>
          <Pill tone={tone as any}>{pct}% 超时</Pill>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>
          {desc} · 阈值 {threshold}
        </div>
      </div>
    </HomeCard>
  );
}

function StageTab({ title, stats }: { title: string; stats: any }) {
  if (!stats) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      <StatCard label="活跃" value={stats.total} unit="客户" />
      <StatCard label="超时" value={stats.overdue} unit="客户" valueColor="danger" />
      <StatCard label="阈值"
                value={stats.threshold_hours ?? stats.threshold_days}
                unit={stats.threshold_hours ? '小时' : '天'} />
    </div>
  );
}

function Ranking({ rows }: { rows: any[] }) {
  return (
    <HomeCard title={`销售员 30 天 L0→L1 通过率排行（${rows.length}）`}>
      <TableWrap>
        <table className="ck-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>排名</th><th>销售员</th><th>分配 30d</th><th>通过 30d</th><th>通过率</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无数据</td></tr>
            )}
            {rows.map((r: any, i: number) => (
              <tr key={r.userId}>
                <td>{i + 1}</td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Avatar userId={r.userId} src={r.avatarUrl} name={r.realName} size="sm" />
                    <span>{r.realName}</span>
                  </span>
                </td>
                <td>{r.assigned}</td>
                <td>{r.converted}</td>
                <td>
                  <Pill tone={r.rate >= 0.7 ? 'success' : r.rate >= 0.4 ? 'warning' : 'danger'}>
                    {(r.rate * 100).toFixed(1)}%
                  </Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </HomeCard>
  );
}
